import { Navigate } from "react-router-dom";
import { getUserData } from "../../api/services/hroneApi";
import { usePermissions } from "../../redux/slices/permissionsSlice";
import { MODULE_VIEW_PERMISSION, MODULES, FEATURE_CODES, HIRING_MANAGEMENT_FEATURE_CODES } from "../../util/featureCodes";
import { getFirstAccessiblePath } from "../../config/navigationConfig";

const employeeAccessiblePaths = [
  "payslip",
  "profile",
  "reimbursement",
  "login",
  "leave_management",
  "/",
  "policies",
  "form16",
  "hiring_openings",
  "hiring_openings_details",
  "hiring_my_referrals",
];
const hrAccessiblePaths = [
  "payslip",
  "profile",
  "reimbursement",
  "login",
  "leave_management",
  "LeavesOverview",
  "/",
  "resource_tracking",
  "reimbursement",
  "allemployees",
  "employee_details",
  "expenditure",
  "policies",
  "email_templates",
  "create_email_template",
  "edit_email_template",
  "form16",
  // Hiring module — HR gets the same full access as Admin/Super Admin here.
  "hiring",
  "hiring_my_referrals",
];

const leadAccessiblePath = [
  "payslip",
  "profile",
  "reimbursement",
  "login",
  "leave_management",
  "LeavesOverview",
  "/",
  "reimbursement",
  "policies",
  "form16",
  "hiring_openings",
  "hiring_openings_details",
  "hiring_my_referrals",
];

const ProtectedRoute = ({ children }) => {
  const path = children.props.path;
  const jwtToken = localStorage.getItem("loginDetails");
  const { hasPermission, hasAnyPermission } = usePermissions();

  if (jwtToken === null) {
    return <Navigate to="/login" />;
  }

  const { role, empId } = getUserData(jwtToken);

  // Dashboard and Leave Management are migrated to permission-based route
  // access. Every other module below still uses its existing legacy role
  // check — unchanged, so non-migrated modules keep current production
  // behavior.
  const isCurrentModuleAccessible = () => {
    if (path === "/") {
      return hasPermission(MODULE_VIEW_PERMISSION[MODULES.DASHBOARD]);
    }
    if (path === "leave_management") {
      return hasPermission(FEATURE_CODES.LEAVE_VIEW_MODULE);
    }
    if (path === "LeavesOverview") {
      return hasPermission(FEATURE_CODES.LEAVE_OVERVIEW_VIEW);
    }
    if (path === "allemployees" || path === "employee_details") {
      return hasPermission(MODULE_VIEW_PERMISSION[MODULES.EMPLOYEE]);
    }
    if (path === "salary") {
      return hasPermission(MODULE_VIEW_PERMISSION[MODULES.SALARY]);
    }
    if (path === "payslip") {
      return hasPermission(MODULE_VIEW_PERMISSION[MODULES.PAYSLIP]);
    }
    if (path === "resource_tracking") {
      return hasPermission(MODULE_VIEW_PERMISSION[MODULES.RESOURCE]);
    }
    if (path === "reimbursement") {
      return hasPermission(MODULE_VIEW_PERMISSION[MODULES.REIMBURSEMENT]);
    }
    if (path === "expenditure") {
      return hasPermission(MODULE_VIEW_PERMISSION[MODULES.EXPENDITURE]);
    }
    if (path === "settings") {
      return hasPermission(MODULE_VIEW_PERMISSION[MODULES.SETTINGS]);
    }
    if (path === "profile") {
      return hasPermission(MODULE_VIEW_PERMISSION[MODULES.PROFILE]);
    }
    if (path === "policies") {
      return hasPermission(MODULE_VIEW_PERMISSION[MODULES.POLICY]);
    }
    if (path === "email_templates") {
      return hasPermission(MODULE_VIEW_PERMISSION[MODULES.EMAIL_TEMPLATE]);
    }
    if (path === "create_email_template") {
      return hasPermission(FEATURE_CODES.EMAIL_TEMPLATE_CREATE);
    }
    if (path === "edit_email_template") {
      return hasPermission(FEATURE_CODES.EMAIL_TEMPLATE_EDIT);
    }
    if (path === "form16") {
      return hasPermission(MODULE_VIEW_PERMISSION[MODULES.FORM16]);
    }
    // Only the HR/Admin management console ("/hiring") is permission-gated
    // here. The Employee/Lead-facing sub-routes (hiring_openings and
    // friends) and the HR-only sub-routes (hiring_job_details etc.) stay on
    // the legacy role arrays below — they already resolve correctly, and
    // giving them the same permission code as "hiring" would let an
    // Employee/Lead with hiring.view (if ever granted) get redirected into
    // the HR console instead of their own openings page.
    if (path === "hiring") {
      return hasPermission(MODULE_VIEW_PERMISSION[MODULES.HIRING]);
    }
    // Employee-facing job-openings screens: reachable by any role that has
    // Hiring access at all (the default baseline), not just Employee/Lead — a
    // custom role with only default Hiring access lands here instead of the
    // HR console (JobListPage redirects it here when it has no management
    // permission).
    if (
      path === "hiring_openings" ||
      path === "hiring_openings_details" ||
      path === "hiring_my_referrals"
    ) {
      return hasPermission(MODULE_VIEW_PERMISSION[MODULES.HIRING]);
    }
    if (
      path === "hiring_job_details" ||
      path === "hiring_job_needs_review" ||
      path === "hiring_job_duplicate" ||
      path === "hiring_job_failed" ||
      path === "hiring_job_applications" ||
      path === "hiring_candidate_details"
    ) {
      return hasAnyPermission(HIRING_MANAGEMENT_FEATURE_CODES);
    }
    if (path === "projects") {
      return hasPermission(MODULE_VIEW_PERMISSION[MODULES.PROJECT]);
    }
    if (path === "clients") {
      return hasPermission(MODULE_VIEW_PERMISSION[MODULES.CLIENT]);
    }
    if (path === "revenue") {
      return hasPermission(MODULE_VIEW_PERMISSION[MODULES.REVENUE]);
    }
    if (path === "invoices") {
      return hasPermission(MODULE_VIEW_PERMISSION[MODULES.INVOICE]);
    }
    if (path === "project_user_association") {
      return hasPermission(MODULE_VIEW_PERMISSION[MODULES.PROJECT]);
    }
    // WorkLogs is used broadly (own logs for Employee/HR, admin dashboards for
    // Lead/Admin/Super Admin) — gate on the single WORKLOG_VIEW code shared by
    // every worklog route, matching how the seed migration grants it to all roles.
    if (
      path === "worklogs" ||
      path === "worklogs_dashboard" ||
      path === "worklogs_admin_dashboard" ||
      path === "worklogs_admin_project_employee_dashboard"
    ) {
      return hasPermission(MODULE_VIEW_PERMISSION[MODULES.WORKLOG]);
    }
    switch (role) {
      case "Admin":
      case "Super Admin":
        return true;
      case "Employee":
        return employeeAccessiblePaths.includes(path);
      case "HR":
        return hrAccessiblePaths.includes(path);
      case "Lead":
        return leadAccessiblePath.includes(path);
      default:
        return false;
    }
  };

  if (isCurrentModuleAccessible()) {
    return children;
  }

  // Denied (module not permitted, or permission revoked since login/refresh):
  // send the user to the first module they can actually reach, computed from
  // the shared navigation config, instead of a hardcoded route.
  return <Navigate to={getFirstAccessiblePath({ role, hasPermission, empId })} />;
};

export default ProtectedRoute;
